import React, { useState } from 'react';
import {
  Modal,
  Form,
  DatePicker,
  Button,
  Space,
  message,
  Typography,
} from 'antd';
import { CalendarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Text } = Typography;

interface ReportGenerationModalProps {
  visible: boolean;
  onCancel: () => void;
  onGenerate: (params: {
    from_date: string;
    to_date: string;
  }) => void;
  loading?: boolean;
}

const ReportGenerationModal: React.FC<ReportGenerationModalProps> = ({
  visible,
  onCancel,
  onGenerate,
  loading = false,
}) => {
  const [form] = Form.useForm();
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      const { dateRange: selectedDateRange } = values;
      
      if (!selectedDateRange || selectedDateRange.length !== 2) {
        message.error('Please select a date range');
        return;
      }

      const [startDate, endDate] = selectedDateRange;
      const today = dayjs();
      
      // Validate that end date is not in the future
      if (endDate.isAfter(today, 'day')) {
        message.error('End date cannot be in the future');
        return;
      }

      // Validate that start date is not after end date
      if (startDate.isAfter(endDate, 'day')) {
        message.error('Start date cannot be after end date');
        return;
      }

      const reportParams = {
        from_date: startDate.format('YYYY/MM/DD'),
        to_date: endDate.format('YYYY/MM/DD'),
      };

      onGenerate(reportParams);
    });
  };

  const handleCancel = () => {
    form.resetFields();
    setDateRange(null);
    onCancel();
  };

  return (
    <Modal
      title={
        <Space>
          <CalendarOutlined />
          Generate Transaction Report
        </Space>
      }
      open={visible}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button key="generate" type="primary" loading={loading} onClick={handleSubmit}>
          Generate Report
        </Button>,
      ]}
      width={400}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label="Select Date Range"
          name="dateRange"
          rules={[{ required: true, message: 'Please select a date range' }]}
        >
          <RangePicker
            style={{ width: '100%' }}
            value={dateRange}
            onChange={(dates) => setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)}
            disabledDate={(current) => {
              // Disable future dates
              return current && current > dayjs().endOf('day');
            }}
            format="YYYY-MM-DD"
          />
        </Form.Item>

        {/* <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
          End date cannot be in the future.
        </Text> */}
      </Form>
    </Modal>
  );
};

export default ReportGenerationModal;
